import { useState } from "react";
import { motion } from "motion/react";
import { Box, Chip, Typography, useTheme } from "@mui/material";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { WorkflowTaskStatus } from "~/operations";
import useWorkflowDetail from "../hooks/useWorkflowDetail";

export default function WorkflowProgress({
  progressData,
}: {
  progressData: ReturnType<typeof useWorkflowDetail>["progressData"];
}) {
  let theme = useTheme();
  const { tasks } = progressData;
  const [animationKey, setAnimationKey] = useState(0);
  const { isFeatureEnabled } = useFeatureFlags();

  // get the percentage, avoid Initial NaN by ensuring at least 1 task
  const getPercentage = (index: number) => {
    const taskLength = Math.max(1, tasks.length - 1);
    return `${Math.round((index / taskLength) * 100)}%`;
  };

  // calculate step percentages based on number of tasks starting from 0% and ending at 100% i.e. ["0%", "50%", "100%"];
  const stepPercentages = tasks.map((_, index) => {
    return getPercentage(index);
  });

  // loop through tasks to determine current step starting from 0
  const currentStep = tasks.reduce((step, task) => {
    if (task.status === WorkflowTaskStatus.Completed) {
      return step + 1;
    }
    return step;
  }, 0);

  // generate keyframes for width animation based on current step i.e. if step is 1, return [0%, 0%, 50%, 50%]
  const generateWidthKeyframes = (step: number) => {
    const keyframes: string[] = [];
    for (let i = 0; i < step; i++) {
      keyframes.push(stepPercentages[i]);
      keyframes.push(stepPercentages[i]); // pause at this stop
    }
    // Remove the last duplicate to end on the final position
    if (keyframes.length > 5) keyframes.pop();
    return keyframes;
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        p: 4,
        pb: 3,
        gap: 1.375,
        borderRadius: 2,
        backgroundColor: "background.paper",
      }}
    >
      {isFeatureEnabled("Developer Tools") && (
        <Chip
          label={"restart animation"}
          color="warning"
          size="small"
          onClick={() => setAnimationKey((prev) => prev + 1)}
          sx={{ position: "absolute", top: -16, left: -16, cursor: "pointer" }}
        />
      )}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "flex-start",
          alignSelf: "stretch",
          height: 4,
          gap: 3,
          borderRadius: 2,
          backgroundColor: "background.light", // demo light background
        }}
      >
        {/* Animated progress bar */}
        <Box
          key={`progress-${animationKey}`}
          component={motion.div}
          initial={{ width: "0%" }}
          animate={{
            width: generateWidthKeyframes(currentStep),
          }}
          transition={{
            delay: 0.2,
            duration: 1.5,
            ease: "easeInOut",
            times: [0, 0.14, 0.43, 0.57, 1],
          }}
          sx={{
            position: "absolute",
            left: -3,
            top: 0,
            height: 4,
            backgroundColor: "primary.main",
            borderRadius: 2,
          }}
        />

        {/* Stop markers */}
        <Box
          key={`markers-${animationKey}`}
          sx={{
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "calc(100% + 6px)",
            left: -3,
            top: 0,
            height: 4,
            pointerEvents: "none",
          }}
        >
          {tasks.map((task, index) => {
            const delay = 0.2 + index * 0.8;
            return (
              <Box
                key={index}
                component={motion.div}
                initial={{
                  scale: 1,
                }}
                animate={{
                  ...(task.status === WorkflowTaskStatus.Completed && {
                    scale: [1, 1.5, 1],
                    backgroundColor: theme.palette.primary.main,
                  }),
                }}
                transition={{
                  delay,
                  duration: 0.5,
                  ease: [0.34, 1.56, 0.64, 1],
                }}
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  borderWidth: 2,
                  borderStyle: "solid",
                  borderColor: "background.paper",
                  backgroundColor: "background.light",
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Labels */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          alignSelf: "stretch",
          gap: 1.25,
        }}
      >
        <Typography variant="subtitle2" sx={{ flex: "1 0 0" }}>
          PR created
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ flex: "1 0 0", textAlign: "center" }}
        >
          PR merged
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ flex: "1 0 0", textAlign: "right", color: "text.disabled" }} // demo light text
        >
          Fix confirmed
        </Typography>
      </Box>
    </Box>
  );
}
